import { useScreenCue } from "./ScreenCue";
import { useExit } from "./useExit";
import { useSheetLayer } from "../devsheet-hooks";
import { useEffect, useState } from "preact/hooks";
import { crownedPortraitUrl, useOriginalAssets } from "../features";
import { enablePush, getPushState, nativePushUndecided, pushSupported } from "../push";
import { isNativeShell } from "./InstallHint";
import { BusyLabel } from "./BusyLabel";

const HIDE_KEY = "op.hideNotifyPrompt";
const SESSION_KEY = "op.notifyPromptSeen";
/** "Not now" puts the prompt away for a week on this device: an epoch ms it stays hidden until. */
const SNOOZE_KEY = "op.notifyPromptSnoozedUntil";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

function snoozed(): boolean {
  try {
    return Date.now() < Number(localStorage.getItem(SNOOZE_KEY) ?? 0);
  } catch {
    return false;
  }
}

function snooze(): void {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
  } catch {
    /* ignore */
  }
}

function read(store: Storage, key: string): boolean {
  try {
    return store.getItem(key) === "1";
  } catch {
    return false;
  }
}

function write(store: Storage, key: string): void {
  try {
    store.setItem(key, "1");
  } catch {
    /* ignore */
  }
}

/**
 * Should the nudge appear now? Only where push can actually work (Android
 * browsers, iOS home-screen installs, the iOS app), only while permission is undecided,
 * and not if the peon has said "don't ask again", dismissed it this open, or put it away for the
 * week with "Not now".
 */
export async function shouldShowNotifyPrompt(): Promise<boolean> {
  if (!pushSupported()) return false;
  // The iOS app's web view has no Notification global; iOS itself says whether it has asked.
  if (isNativeShell()) {
    if (!(await nativePushUndecided())) return false;
  } else if (Notification.permission !== "default") return false;
  if (read(localStorage, HIDE_KEY) || read(sessionStorage, SESSION_KEY) || snoozed()) return false;
  return (await getPushState()) === "off";
}

/** One-time sheet asking to turn on notifications for new tasks. */
export function NotifyPrompt({ name, onClose }: { name: string; onClose: () => void }) {
  // Escape, the backdrop and the close buttons leave through the stepped exit (src/components/useExit.ts).
  const exit = useExit(onClose);
  useScreenCue(exit.closing);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dontAsk, setDontAsk] = useState(false);
  const originalAssets = useOriginalAssets();

  useEffect(() => write(sessionStorage, SESSION_KEY), []);

  // Put away without "Don't ask again" (Not now, the backdrop, Escape): back in a week.
  function close() {
    if (dontAsk) write(localStorage, HIDE_KEY);
    else snooze();
    exit.close();
  }
  const sheetRef = useSheetLayer(!exit.closing, close);

  async function turnOn() {
    setBusy(true);
    setError(null);
    try {
      const state = await enablePush(name);
      if (state === "on" || state === "blocked") write(localStorage, HIDE_KEY); // decided either way
      exit.close();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not turn on notifications");
      setBusy(false);
    }
  }

  return (
    <div class={`sheet-backdrop${exit.cls}`} ref={exit.ref} onClick={close}>
      <div class="sheet" ref={sheetRef} role="dialog" aria-modal="true" tabIndex={-1} aria-label="Notifications" onClick={(e) => e.stopPropagation()}>
        <img class="sheet-icon" src={crownedPortraitUrl(originalAssets)} alt="" />
        <h2>Hear when there's work</h2>
        <p class="muted">A notification when a chore needs doing. Change it any time by tapping your name.</p>
        <button type="button" class="btn btn-primary btn-block" onClick={turnOn} disabled={busy}>
          <BusyLabel busy={busy} label="Asking…">Turn on notifications</BusyLabel>
        </button>
        {error && <div class="form-error" role="alert">{error}</div>}
        <label class="field toggle">
          <input type="checkbox" checked={dontAsk} onChange={(e) => setDontAsk(e.currentTarget.checked)} />
          <span>Don't ask again on this device</span>
        </label>
        <button type="button" class="btn btn-ghost btn-block" onClick={close} disabled={busy}>
          Not now
        </button>
      </div>
    </div>
  );
}
